import { ChevronDown, ChevronUp, Copy, Plus, Trash2 } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { SECTION_TYPES } from "../constants/themes";

/**
 * Toolbar for managing the sections list.
 * Provides add (dropdown), reorder (arrows), duplicate, and delete.
 *
 * @param {Object} props
 * @param {Array} props.sections - Current sections array
 * @param {Function} props.onAdd - (newSection) => void
 * @param {Function} props.onMove - (fromIdx, toIdx) => void
 * @param {Function} props.onDuplicate - (idx) => void
 * @param {Function} props.onRemove - (idx) => void
 * @param {Function} [props.onNotify] - (message, type) => void for validation messages
 */
export default function SectionToolbar({
	sections,
	onAdd,
	onMove: _onMove,
	onDuplicate: _onDuplicate,
	onRemove: _onRemove,
	onNotify,
}) {
	const [showDropdown, setShowDropdown] = useState(false);
	const dropdownRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setShowDropdown(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleAddSection = (type) => {
		// Validate max:1 for company_intro
		if (
			type.max &&
			sections.filter((s) => s.type === type.value).length >= type.max
		) {
			onNotify?.(
				`Ja existe uma secao "${type.label}" (maximo ${type.max})`,
				"error",
			);
			setShowDropdown(false);
			return;
		}

		const defaults = {
			company_intro: {
				type: "company_intro",
				title: "Quem Somos",
				badge: "Sobre a Empresa",
				icon: "star",
				text_align: "left",
				bg_style: "default",
				padding: "md",
			},
			custom: {
				type: "custom",
				title: "",
				content: "",
				layout: "full-width",
				text_align: "left",
				bg_style: "default",
				padding: "md",
			},
			features_grid: {
				type: "features_grid",
				title: "",
				badge: "",
				icon: "star",
				cards: [],
				columns: 3,
				text_align: "center",
				bg_style: "default",
				padding: "md",
			},
			image_gallery: {
				type: "image_gallery",
				title: "",
				badge: "",
				images: [],
				columns: 2,
				text_align: "center",
				bg_style: "default",
				padding: "md",
			},
			cta: {
				type: "cta",
				title: "",
				content: "",
				badge: "",
				icon: "zap",
				text_align: "center",
				bg_style: "accent",
				padding: "lg",
			},
		};

		onAdd(defaults[type.value] || defaults.custom);
		setShowDropdown(false);
	};

	return (
		<div className="flex items-center justify-between mb-3">
			<h3 className="text-sm font-semibold text-white">
				Secoes ({sections.length})
			</h3>
			<div className="relative" ref={dropdownRef}>
				<button
					type="button"
					onClick={() => setShowDropdown(!showDropdown)}
					className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600/20 hover:bg-cyan-600/30 border border-cyan-500/30 text-cyan-300 text-xs font-medium transition-all"
				>
					<Plus className="w-3.5 h-3.5" /> Adicionar Secao
				</button>
				{showDropdown && (
					<div className="absolute right-0 top-full mt-1 z-20 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 min-w-[200px]">
						{SECTION_TYPES.map((type) => {
							const Icon = type.icon;
							const disabled =
								type.max &&
								sections.filter((s) => s.type === type.value).length >=
									type.max;
							return (
								<button
									key={type.value}
									type="button"
									onClick={() => !disabled && handleAddSection(type)}
									disabled={disabled}
									className={`w-full text-left px-4 py-2 flex items-center gap-2.5 text-sm transition-all ${
										disabled
											? "text-slate-600 cursor-not-allowed"
											: "text-slate-300 hover:bg-slate-700/50 hover:text-white"
									}`}
								>
									<Icon className="w-4 h-4 flex-shrink-0" />
									<span>{type.label}</span>
									{disabled && (
										<span className="text-[10px] text-slate-600 ml-auto">
											(max)
										</span>
									)}
								</button>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}

SectionToolbar.propTypes = {
	sections: PropTypes.array.isRequired,
	onAdd: PropTypes.func.isRequired,
	onMove: PropTypes.func,
	onDuplicate: PropTypes.func,
	onRemove: PropTypes.func,
	onNotify: PropTypes.func,
};

/**
 * Per-section action buttons (move, duplicate, delete).
 */
export function SectionActions({
	index,
	total,
	onMove,
	onDuplicate,
	onRemove,
}) {
	return (
		<div className="flex items-center gap-1">
			<button
				type="button"
				onClick={() => onMove(index, index - 1)}
				disabled={index === 0}
				className="p-1 rounded text-slate-500 hover:text-white hover:bg-slate-700/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
				title="Mover para cima"
			>
				<ChevronUp className="w-3.5 h-3.5" />
			</button>
			<button
				type="button"
				onClick={() => onMove(index, index + 1)}
				disabled={index === total - 1}
				className="p-1 rounded text-slate-500 hover:text-white hover:bg-slate-700/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
				title="Mover para baixo"
			>
				<ChevronDown className="w-3.5 h-3.5" />
			</button>
			<button
				type="button"
				onClick={() => onDuplicate(index)}
				className="p-1 rounded text-slate-500 hover:text-cyan-400 hover:bg-slate-700/50 transition-all"
				title="Duplicar"
			>
				<Copy className="w-3.5 h-3.5" />
			</button>
			<button
				type="button"
				onClick={() => onRemove(index)}
				className="p-1 rounded text-slate-500 hover:text-red-400 hover:bg-slate-700/50 transition-all"
				title="Remover"
			>
				<Trash2 className="w-3.5 h-3.5" />
			</button>
		</div>
	);
}

SectionActions.propTypes = {
	index: PropTypes.number.isRequired,
	total: PropTypes.number.isRequired,
	onMove: PropTypes.func.isRequired,
	onDuplicate: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
};
